import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import type { BlogPost } from '@/types/blog';

interface SendToChatGPTButtonProps {
  post: BlogPost;
  className?: string;
}

/**
 * Formats a blog post as a ChatGPT prompt
 * Creates a context-rich prompt for AI analysis
 */
function formatPostForChatGPT(post: BlogPost): string {
  const tags = post.tags || [];
  
  const prompt = `Analise este artigo do meu blog e forneça insights:

# ${post.title}

**Categorias:** ${post.categories.join(', ')}
${tags.length > 0 ? `**Tags:** ${tags.join(', ')}` : ''}
**Data:** ${post.date.toLocaleDateString('pt-BR')}

## Resumo
${post.excerpt}

## Conteúdo Completo
${post.content}

---

Por favor:
1. Resuma os principais pontos do artigo
2. Sugira melhorias ou tópicos relacionados
3. Identifique possíveis perguntas dos leitores
4. Recomende próximos artigos sobre este tema`;

  return prompt;
}

/**
 * Button component to send blog post to ChatGPT
 * Opens ChatGPT in a new tab with pre-filled post content
 * Follows UI/UX AI Pattern 4: Direct LLM Integration
 */
export function SendToChatGPTButton({ post, className = '' }: SendToChatGPTButtonProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const handleSendToChatGPT = async () => {
    try {
      console.log('🤖 Enviando post para ChatGPT...', post.title);
      const prompt = formatPostForChatGPT(post);
      console.log('📝 Prompt gerado:', prompt.substring(0, 200) + '...');
      
      // Copy to clipboard
      await navigator.clipboard.writeText(prompt);
      console.log('📋 Prompt copiado para clipboard');
      
      // Show tooltip
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
      
      // Open ChatGPT in new tab after a short delay
      setTimeout(() => {
        window.open('https://chatgpt.com/', '_blank', 'noopener,noreferrer');
        console.log('✅ ChatGPT aberto! Cole o conteúdo (Ctrl+V / Cmd+V)');
      }, 300);
      
    } catch (error) {
      console.error('❌ Erro ao enviar para ChatGPT:', error);
      alert('Erro ao copiar. Verifique as permissões do navegador.');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleSendToChatGPT}
        className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all shadow-sm ${className}`}
        title="Copiar e abrir ChatGPT"
        aria-label="Copiar post e abrir ChatGPT"
      >
        <MessageSquare className="w-4 h-4" />
        <span className="hidden sm:inline">ChatGPT</span>
      </button>
      
      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg whitespace-nowrap z-50 animate-in fade-in slide-in-from-top-1">
          ✅ Copiado! Cole no ChatGPT (Ctrl+V)
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
        </div>
      )}
    </div>
  );
}
